import React, { useState, useEffect } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { Button } from '@/components/ui/button';
import {
  ClientSelector,
  ProjectSelector,
  AlertMessage,
  UploadProgress,
} from '@/features/add-scan/components';
import { DroneTypeSelector, DateInput, ZipFileUpload } from '../components';
import { useClients, useProjects } from '@/features/add-scan/hooks';
import {
  getDroneTypes,
  getSignedUrlForDroneZip,
  uploadZipToGCS,
  createDroneVideo,
} from '../api/drone-api';
import { DroneTypeChoice } from '../types';

export const UploadDroneForm: React.FC = () => {
  const { user } = useUser();

  // Form state
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedDroneType, setSelectedDroneType] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Drone types
  const [droneTypes, setDroneTypes] = useState<DroneTypeChoice[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data hooks
  const { clients } = useClients(user?.is_staff || false);
  const { projects, isLoading: isLoadingProjects } = useProjects(selectedClient);

  // Load drone types on mount
  useEffect(() => {
    const loadDroneTypes = async () => {
      setIsLoadingTypes(true);
      try {
        const types = await getDroneTypes();
        console.log('📋 Drone types loaded:', types);
        console.log('📋 Is array?', Array.isArray(types));
        setDroneTypes(types);
      } catch (err) {
        console.error('❌ Error loading drone types:', err);
        setError('Failed to load drone types');
        setDroneTypes([]); // Set to empty array on error
      } finally {
        setIsLoadingTypes(false);
      }
    };

    loadDroneTypes();
  }, []);

  // Convert YYYY-MM-DD to MM.DD.YY format for backend
  const formatDateForBackend = (dateStr: string): string => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const shortYear = year.slice(-2);
    return `${month}.${day}.${shortYear}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Form submission started');
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedClient || !selectedProject || !selectedDroneType || !date) {
      console.error('❌ Validation failed: Missing required fields');
      setError('Please fill in all required fields');
      return;
    }

    if (!selectedFile) {
      console.error('❌ Validation failed: No file selected');
      setError('Please select a zip file to upload');
      return;
    }

    console.log('📋 Form data:', {
      selectedClient,
      selectedProject,
      selectedDroneType,
      date,
      fileName: selectedFile.name
    });

    try {
      setIsUploading(true);
      setUploadStatus('Preparing upload...');
      setUploadProgress(0);

      // Convert date to backend format (MM.DD.YY)
      const formattedDate = formatDateForBackend(date);
      console.log('📅 Formatted date:', formattedDate);

      // Step 1: Get signed URL
      console.log('🔑 Step 1: Requesting signed URL...');
      setUploadStatus('Getting upload URL...');
      const signedUrlData = await getSignedUrlForDroneZip(
        parseInt(selectedProject),
        selectedDroneType,
        formattedDate,
        selectedFile.name
      );
      console.log('✅ Step 1: Signed URL received:', signedUrlData.gcs_url);

      // Step 2: Upload zip to GCS
      console.log('📤 Step 2: Uploading to GCS...');
      setUploadStatus('Uploading zip file...');
      await uploadZipToGCS(selectedFile, signedUrlData.signed_url, (progress) => {
        setUploadProgress(progress);
        setUploadStatus(`Uploading zip file: ${Math.floor(progress)}%`);
      });
      console.log('✅ Step 2: Upload to GCS complete');

      // Step 3: Create drone video record (backend will extract zip)
      console.log('💾 Step 3: Creating drone video record...');
      setUploadStatus('Processing drone video...');
      setUploadProgress(100);

      const result = await createDroneVideo({
        project: parseInt(selectedProject),
        drone_type: selectedDroneType,
        date: formattedDate,
        uploaded_zip_url: signedUrlData.gcs_url,
      });
      console.log('✅ Step 3: Result:', result);

      if (result.success) {
        console.log('🎉 Upload successful!');
        setSuccess(result.message || 'Drone video uploaded successfully!');
        
        // Clear form fields except client/project
        setSelectedDroneType('');
        setDate('');
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStatus('');
      } else {
        console.error('❌ Backend returned error:', result);
        setError(result.error || 'Failed to create drone video');
      }
    } catch (err: any) {
      console.error('❌ Error uploading drone video:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        responseStatus: err.response?.status,
        responseHeaders: err.response?.headers,
        fullError: err
      });
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload drone video';
      setError(errorMessage);
    } finally {
      console.log('🏁 Upload process finished');
      setIsUploading(false);
    }
  };

  // Check if user is authenticated and has permission
  if (!user) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>
        <AlertMessage
          type="error"
          message="You must be logged in to access this page. Please log in and try again."
        />
      </div>
    );
  }

  if (!user.is_staff) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>
        <AlertMessage
          type="error"
          message="You must be a staff member to upload drone videos. Please contact your administrator for access."
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
      <h1 className="text-3xl font-bold mb-6">Upload Drone Video</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <ClientSelector
          clients={clients}
          value={selectedClient}
          onChange={setSelectedClient}
        />

        <ProjectSelector
          projects={projects}
          value={selectedProject}
          onChange={setSelectedProject}
          disabled={!selectedClient}
          isLoading={isLoadingProjects}
        />

        <DroneTypeSelector
          droneTypes={droneTypes}
          value={selectedDroneType}
          onChange={setSelectedDroneType}
          disabled={!selectedProject}
          isLoading={isLoadingTypes}
        />

        <DateInput
          value={date}
          onChange={setDate}
          disabled={isUploading}
        />

        <ZipFileUpload
          selectedFile={selectedFile}
          onChange={setSelectedFile}
          disabled={isUploading}
        />

        {isUploading && (
          <UploadProgress progress={uploadProgress} status={uploadStatus} />
        )}

        <Button
          type="submit"
          disabled={isUploading}
          className="w-full"
          onClick={() => {
            console.log('🖱️ Button clicked');
            // Don't call handleSubmit here - let the form onSubmit handle it
          }}
        >
          {isUploading ? 'Uploading...' : 'Upload Drone Video'}
        </Button>

        {error && <AlertMessage type="error" message={error} />}
        {success && <AlertMessage type="success" message={success} />}
      </form>
    </div>
  );
};

